import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Minus, Send, X } from "lucide-react";
import api, { apiError, buildWsUrl } from "@/lib/api";
import { forgetOpenChat, listOpenChats } from "@/lib/chatSession";

export default function ChatDock() {
  const location = useLocation();
  const [chats, setChats] = useState(() => listOpenChats());

  useEffect(() => {
    const sync = () => setChats(listOpenChats());
    window.addEventListener("stb-chats-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("stb-chats-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (location.pathname.startsWith("/doklad/") || location.pathname.startsWith("/admin")) {
    return null;
  }

  const visible = chats.filter((c) => location.pathname !== `/zpravy/${c.roomId}`);
  if (!visible.length) return null;

  return (
    <div className="fixed bottom-3 right-3 z-50 flex flex-col-reverse items-end gap-2 pointer-events-none">
      {visible.map((c) => (
        <div key={c.roomId} className="pointer-events-auto">
          <DockWindow chat={c} />
        </div>
      ))}
    </div>
  );
}

function DockWindow({ chat }) {
  const [open, setOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [myIdentity, setMyIdentity] = useState(null);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState("");
  const [onlineRoles, setOnlineRoles] = useState({ buyer: false, seller: false });
  const [myRole, setMyRole] = useState(null);
  const wsRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/chats/${chat.roomId}/messages`, {
          params: { token: chat.token },
        });
        if (cancelled) return;
        setMessages(data.items || []);
        setMyIdentity(data.my_identity);
        setMyRole(data.my_role);
      } catch (e) {
        if (!cancelled) setError(apiError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chat.roomId, chat.token]);

  useEffect(() => {
    const url = buildWsUrl(`/chats/ws/${chat.roomId}`, chat.token);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.event === "message" && data.data) {
          setMessages((prev) => (prev.some((m) => m.id === data.data.id) ? prev : [...prev, data.data]));
          if (data.data.sender_id !== myIdentity) {
            setUnread((n) => n + 1);
          }
        }
        if (data.event === "presence" && data.roles) {
          setOnlineRoles({ buyer: !!data.roles.buyer, seller: !!data.roles.seller });
        }
      } catch (_) {}
    };
    return () => {
      try { ws.close(); } catch (_) {}
    };
  }, [chat.roomId, chat.token, myIdentity]);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      setUnread(0);
    }
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    const body = { type: "text", text: t };
    try {
      if (wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify(body));
      } else {
        const { data } = await api.post(`/chats/${chat.roomId}/messages`, body, {
          params: { token: chat.token },
        });
        if (data.message) {
          setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
        }
      }
      setText("");
    } catch (err) {
      setError(apiError(err) || "Zprávu se nepodařilo odeslat.");
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="stb-btn stb-btn-primary relative shadow-lg"
        data-testid="chat-dock-bubble"
      >
        Chat · {chat.otherName}
        {unread > 0 && (
          <span className="absolute -top-2 -right-2 bg-[#0E0E0E] text-[#FF6B00] border-2 border-[#FF6B00] stb-mono text-[10px] min-w-[1.25rem] h-5 px-1 flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <section
      className="w-[320px] max-w-[92vw] bg-[#0E0E0E] border-2 border-[#262626] shadow-2xl flex flex-col h-[380px]"
      data-testid="chat-dock-window"
    >
      <header className="p-2 border-b-2 border-[#262626] flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="stb-label truncate">
            Chat · {chat.otherName}
            <span className={`ml-2 stb-mono text-[10px] ${(myRole === "seller" ? onlineRoles.buyer : onlineRoles.seller) ? "text-[#22C55E]" : "text-[#A3A3A3]"}`}>
              {(myRole === "seller" ? onlineRoles.buyer : onlineRoles.seller) ? "● u chatu" : "○ není u chatu"}
            </span>
          </p>
          {chat.listingId ? (
            <Link to={`/inzerat/${chat.listingId}`} className="text-white text-xs truncate block hover:text-[#FF6B00]">
              {chat.listingTitle}
            </Link>
          ) : (
            <p className="text-white text-xs truncate">{chat.listingTitle}</p>
          )}
        </div>
        <button type="button" className="stb-btn stb-btn-ghost !px-2" onClick={() => setOpen(false)} aria-label="Sbalit">
          <Minus size={14} />
        </button>
        <button
          type="button"
          className="stb-btn stb-btn-ghost !px-2"
          onClick={() => forgetOpenChat(chat.roomId)}
          aria-label="Zavřít do zavření prohlížeče"
        >
          <X size={14} />
        </button>
      </header>
      <div ref={scrollerRef} className="flex-1 p-2 space-y-2 overflow-y-auto">
        {messages.slice(-40).map((m) => {
          const own = m.sender_id === myIdentity;
          return (
            <div key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] border px-2 py-1 text-xs ${own ? "bg-[#FF6B00] text-[#0E0E0E] border-[#FF6B00]" : "bg-[#1a1a1a] text-white border-[#262626]"}`}>
                {m.type === "text" && <p className="whitespace-pre-wrap">{m.text}</p>}
                {m.type === "image" && <img src={m.data_url} alt="" className="max-h-32 object-cover" />}
                {m.type === "audio" && <audio controls src={m.data_url} className="w-40" />}
              </div>
            </div>
          );
        })}
      </div>
      {error && <p className="px-2 text-[#FF6B00] text-[10px]">{error}</p>}
      <form onSubmit={send} className="p-2 border-t-2 border-[#262626] flex gap-1">
        <input
          className="stb-input flex-1 !py-1 text-sm"
          placeholder="Napiš zprávu…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="chat-dock-input"
        />
        <button className="stb-btn stb-btn-primary !px-2" disabled={!text.trim()} data-testid="chat-dock-send">
          <Send size={14} />
        </button>
      </form>
    </section>
  );
}
